import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  listBanQueue,
  identityKindDoAlvo,
  rotuloDaIdentidade,
  type BanCandidate,
  type BanQueueResponse,
  type MinTier,
} from '../../services/banQueue';
import { bulkSanctions, type CreateSanctionInput } from '../../services/sanctions';
import { ordenarFilaDeBan, rotuloDoTier, corDoTier } from '../../lib/evidence';
import { EvidenceLine } from '../../components/EvidenceLine';
import {
  PERIOD_KEYS,
  rotuloDoPeriodo,
  rotuloDaJanela,
  type PeriodKey,
} from '../../lib/period';

// ============================================================
// FILA DE BAN — a tela em que o dono decide
// ============================================================
// "contas com evidência forte no período, ordenadas por gravidade, com a PROVA
// visível sem precisar clicar. O operador tem de conseguir decidir olhando a
// linha."
//
// POR QUE CARTÃO E NÃO TABELA
//   Sanctions e Incidents são tabelas porque cada linha é um punhado de
//   escalares. Aqui a linha PRECISA carregar várias frases de prova
//   ("fmod.dll — esperado 1e08da16… × encontrado 894a9bfc…"). Espremer isso
//   numa célula produziria a mesma coisa que já existe e não serve: um
//   contador, com a prova escondida atrás de um clique. É a forma que a página
//   de Events já usa, pela mesma razão.
//
// O NÚMERO QUE NINGUÉM PEDIU E QUE TEM QUE ESTAR NA TELA
//   `signals.unattributed` — evidência que o Oráculo VIU e não sabe de quem é.
//   Uma fila que parece completa quando não está é o mesmo silêncio que deixou
//   `account_id` em 0 de 45 sessões por meses sem ninguém notar. Fica no
//   cabeçalho, não escondido num tooltip.
// ============================================================

function chave(c: BanCandidate): string {
  return `${c.target_kind}:${c.target_value}`;
}

function TargetBadge({ kind }: { kind: BanCandidate['target_kind'] }) {
  const cores: Record<BanCandidate['target_kind'], string> = {
    account: 'bg-emerald-900 text-emerald-300 border-emerald-700',
    user: 'bg-sky-900 text-sky-300 border-sky-700',
  };
  const titulos: Record<BanCandidate['target_kind'], string> = {
    account: 'Identidade de CONTA — o alvo mais preciso que existe',
    user: 'Identidade de JOGADOR — a sessão não reportou a conta separadamente',
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cores[kind]}`}
      title={titulos[kind]}
    >
      {rotuloDaIdentidade(kind)}
    </span>
  );
}

function StatCell({ label, value, sub, accent }: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'normal' | 'warn' | 'danger';
}) {
  const color =
    accent === 'danger' ? 'text-red-300' :
    accent === 'warn'   ? 'text-amber-300' :
    'text-gray-100';
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</div>
      <div className={`text-xl font-bold ${color} tabular-nums mt-0.5`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ============================================================
// O CARTÃO — o MESMO para as duas listas, e é por isso que ele é um componente
// ============================================================
// 🔴 A PROVA APARECE INTEIRA NOS DOIS LADOS, DE PROPÓSITO. A tentação seria
//   mostrar menos no candidato não verificado ("não posso agir, então não
//   preciso ver"). É o contrário: é justamente ali que o operador precisa
//   olhar, porque é ali que estão tanto o cheater que ainda não temos como
//   nomear quanto o inocente que alguém tentou incriminar. O que muda entre as
//   duas listas é só a AÇÃO — a caixa de seleção existe em uma e não existe na
//   outra.
function CandidateCard({
  c, selecionavel, selected, onToggle,
}: {
  c: BanCandidate;
  selecionavel: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
          const kind = identityKindDoAlvo(c.target_kind);
          return (
            <div
              key={chave(c)}
              className={`bg-[#161a1f] border rounded p-3 transition ${
                c.top_tier === 'confirmed'
                  ? 'border-red-800/70 hover:border-red-700'
                  : 'border-[#2a2f36] hover:border-[#3a3f46]'
              } ${c.active_sanction ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* 🔴 SEM IDENTIDADE VERIFICADA NÃO EXISTE CAIXA DE SELEÇÃO.
                    Não é `disabled`: é AUSENTE. Um checkbox desabilitado ainda
                    ensina que existe um caminho — e o caminho não existe. */}
                {selecionavel ? (
                  <input
                    type="checkbox"
                    className="mt-1"
                    disabled={!!c.active_sanction}
                    checked={selected}
                    onChange={onToggle}
                    aria-label={`Selecionar ${c.target_value}`}
                  />
                ) : (
                  <span
                    className="mt-1 text-[11px] text-amber-400"
                    title="Identidade AUTO-DECLARADA pelo cliente: investigação, nunca ban"
                  >
                    ⚠
                  </span>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <TargetBadge kind={c.target_kind} />
                    <Link
                      to={`/accounts/${kind}/${encodeURIComponent(c.target_value)}`}
                      className="font-mono text-sm text-gray-100 hover:text-red-300 hover:underline break-all"
                    >
                      {c.target_value}
                    </Link>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${corDoTier(c.top_tier)}`}>
                      {rotuloDoTier(c.top_tier)}
                    </span>
                    {c.identity_trust === 'declared' && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded border border-amber-700 bg-amber-950/40 text-amber-300"
                        title="Quem disse que esta é a conta foi o CLIENTE (ORACULO_SetAccountId), não o servidor do jogo. Não dá para banir com base nisto."
                      >
                        identidade não verificada
                      </span>
                    )}
                    {c.kick_eligible && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded border border-red-700 bg-red-950/50 text-red-300"
                        title="Uma família Confirmed ou duas Strong — o motor já expulsaria por isto"
                      >
                        sustenta expulsão
                      </span>
                    )}
                    {c.active_sanction && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded border border-gray-600 bg-gray-800 text-gray-300"
                        title={`${c.active_sanction.sanction_type} desde ${c.active_sanction.created_at}`}
                      >
                        já sancionado ({c.active_sanction.sanction_type})
                      </span>
                    )}
                    <span className="text-xs text-gray-500 ml-auto tabular-nums">
                      risco <strong className="text-orange-300">{c.evidence_score}</strong>
                      {' · '}{c.families.confirmed} confirmada(s) / {c.families.strong} forte(s)
                      {' · '}{c.total_hits} ocorrência(s)
                      {' · '}{c.session_uuids.length} sessão(ões)
                    </span>
                  </div>

                  {/* A PROVA */}
                  {c.evidence.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                      Sem prova anexada — abrir o histórico antes de decidir.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {c.evidence.map((ev, i) => (
                        <EvidenceLine key={ev.sample?.event_id || `${c.target_value}-${i}`} sinal={ev} />
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-gray-500">
                    {c.first_seen && (
                      <span title={c.first_seen}>
                        desde {new Date(c.first_seen).toLocaleString()}
                      </span>
                    )}
                    {c.last_seen && (
                      <span title={c.last_seen}>
                        último {new Date(c.last_seen).toLocaleString()}
                      </span>
                    )}
                    <Link
                      to={`/accounts/${kind}/${encodeURIComponent(c.target_value)}`}
                      className="text-blue-400 hover:underline"
                    >
                      histórico por dia/semana/mês →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
}

export function BanQueue() {
  const [period, setPeriod] = useState<PeriodKey>('week');
  const [minTier, setMinTier] = useState<MinTier>('strong');
  const [resp, setResp] = useState<BanQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listBanQueue(period, minTier)
      .then((r) => { if (!cancelled) setResp(r); })
      .catch((e) => {
        if (!cancelled) {
          setResp(null);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, minTier, refreshTick]);

  // Reordena mesmo com o backend já ordenando — ver lib/evidence.ts: os
  // critérios são os DELE, transcritos, mais a divergência declarada de jogar
  // o já-sancionado para o fim.
  //
  // 🔴 2026-08-08 — O `.filter()` NÃO É REDUNDANTE COM O BACKEND, E É DE
  //   PROPÓSITO. O backend já separa `data` (autenticado) de `investigate`
  //   (declarado). Este filtro é a segunda tranca, do lado que desenha o botão:
  //   a tela só monta caixa de seleção sobre identidade verificada, e nenhuma
  //   mudança futura no formato da resposta consegue reintroduzir um botão de
  //   banir sobre um alvo que o próprio CLIENTE escolheu. Custa uma linha; o
  //   erro que ele impede custou uma rodada inteira.
  const candidatos = useMemo(
    () => ordenarFilaDeBan(
      (resp?.data ?? []).filter((c) => c.identity_trust === 'authenticated'),
    ),
    [resp],
  );

  // A outra metade: evidência forte que NÃO sabemos de quem é. Mostrada
  // inteira, com prova, e sem NENHUMA ação — é investigação.
  const investigar = useMemo(
    () => ordenarFilaDeBan(resp?.investigate ?? []),
    [resp],
  );

  const elegiveis = useMemo(
    () => candidatos.filter((c) => !c.active_sanction),
    [candidatos],
  );

  const selecionados = useMemo(
    () => elegiveis.filter((c) => selected.has(chave(c))),
    [elegiveis, selected],
  );

  const kickables = useMemo(
    () => elegiveis.filter((c) => c.kick_eligible),
    [elegiveis],
  );

  function toggle(c: BanCandidate): void {
    const k = chave(c);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelected(next);
  }

  async function aplicar(tipo: 'ban_temp' | 'ban_perm'): Promise<void> {
    if (selecionados.length === 0) return;
    const temporario = tipo === 'ban_temp';
    const rotulo = temporario ? 'suspensão de 24 horas' : 'ban permanente';

    // Quem NÃO é kick_eligible não tem, pelo motor de risco, evidência que
    // sustentaria expulsão automática. Pode ser banido — a decisão é do
    // operador — mas ele tem que saber que está indo além do que a máquina
    // afirma sozinha. Esconder isso transformaria o botão num carimbo.
    const semLastro = selecionados.filter((c) => !c.kick_eligible);

    const motivo = window.prompt(
      `Motivo da ${rotulo} (aplicado em todos os ${selecionados.length} alvos):`,
      temporario ? 'suspensão manual por 24h' : 'ban wave manual',
    );
    if (!motivo) return;

    const aviso = semLastro.length > 0
      ? `\n\n⚠️ ${semLastro.length} alvo(s) NÃO sustentam expulsão automática: falta uma segunda família de evidência forte. Confira a prova antes.`
      : '';
    if (!window.confirm(`Confirma ${rotulo} em ${selecionados.length} alvo(s)?${aviso}`)) return;

    setBulkBusy(true);
    try {
      const inputs: CreateSanctionInput[] = selecionados.map((c) => ({
        sanction_type: tipo,
        // `target_kind` já vem no vocabulário de `sanctions` ('account' |
        // 'user' — ambos aceitos pelo CHECK da tabela). Não há tradução a
        // fazer, e inventar uma aqui seria onde fila e sanção divergiriam.
        target_kind: c.target_kind,
        target_value: c.target_value,
        reason: motivo,
        source_session_uuid: c.session_uuids[0],
        active_until_epoch: temporario
          ? Math.floor(Date.now() / 1000) + (24 * 60 * 60)
          : undefined,
      }));
      const r = await bulkSanctions(inputs);
      window.alert(
        `Aplicado:\n  Criadas: ${r.created}\n  Duplicadas (ignoradas): ${r.skipped_duplicate}\n  Falhas: ${r.failed}`,
      );
      setSelected(new Set());
      setRefreshTick((t) => t + 1);
    } catch (e) {
      window.alert(`Falha: ${e instanceof Error ? e.message : 'erro'}`);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div>
      <header className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-200">Fila de ban</h2>
            <p className="text-xs text-gray-500 mt-1">
              Contas com evidência forte {rotuloDaJanela(period)}, mais grave primeiro.
              A prova está na linha — se ela não convence, não banir.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {PERIOD_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { setPeriod(k); setSelected(new Set()); }}
                className={`px-3 py-1.5 text-sm rounded border ${
                  period === k
                    ? 'border-red-700 bg-red-950/40 text-red-300'
                    : 'border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-gray-800'
                }`}
              >
                {rotuloDoPeriodo(k)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRefreshTick((t) => t + 1)}
              className="px-3 py-1.5 text-sm rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-gray-800"
            >
              Refresh
            </button>
          </div>
        </div>
      </header>

      {resp && (
        <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCell label="Candidatos" value={resp.counts.candidates} />
            <StatCell label="Confirmados" value={resp.counts.confirmed} accent="danger"
                      sub="alteração confirmada" />
            <StatCell label="Fortes" value={resp.counts.strong} accent="warn" />
            <StatCell label="Sustentam expulsão" value={resp.counts.kick_eligible}
                      sub="pelo motor de risco" />
            <StatCell
              label="Podem ser banidos"
              value={resp.counts.ban_actionable}
              accent={resp.counts.ban_actionable === 0 ? 'warn' : 'normal'}
              sub="evidência forte E identidade verificada"
            />
            <StatCell
              label="Sem dono"
              value={resp.signals.unattributed}
              accent={resp.signals.unattributed > 0 ? 'warn' : 'normal'}
              sub={`de ${resp.signals.considered} sinais`}
            />
          </div>
          {resp.counts.declared > 0 && (
            <div className="mt-3 text-xs text-amber-400">
              ⚠️ {resp.counts.declared} alvo(s) com evidência forte têm identidade
              AUTO-DECLARADA pelo cliente ({resp.counts.declared_kick_eligible} deles
              sustentariam expulsão). Eles estão na seção de investigação, no fim da
              página, e não podem ser banidos daqui: declarar a conta de outra pessoa
              custa dois requests HTTP, então banir por essa atribuição é banir quem o
              atacante escolher. Some quando o servidor do jogo confirmar o login.
            </div>
          )}
          {resp.signals.unattributed > 0 && (
            <div className="mt-3 text-xs text-amber-400">
              ⚠️ {resp.signals.unattributed} sinal(is) foram vistos e NÃO puderam ser
              atribuídos a ninguém — sessão sem identidade na época do evento.
              Isso não é ruído: é detecção que existe e não vira ban.
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            Piso de evidência
            <select
              value={minTier}
              onChange={(e) => { setMinTier(e.target.value as MinTier); setSelected(new Set()); }}
              className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
            >
              <option value="strong">forte ou acima</option>
              <option value="confirmed">só alteração confirmada</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => setSelected(new Set(kickables.map(chave)))}
            disabled={kickables.length === 0}
            className="px-3 py-1.5 text-xs rounded border border-red-800 bg-red-950/30 text-red-300 hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Seleciona quem o motor de risco já considera suficiente para expulsão"
          >
            Selecionar quem sustenta expulsão ({kickables.length})
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {selecionados.length} selecionado(s) • {elegiveis.length} sem sanção ativa
          </span>
          <button
            type="button"
            onClick={() => aplicar('ban_temp')}
            disabled={selecionados.length === 0 || bulkBusy}
            className="px-4 py-2 text-sm rounded border border-amber-700 bg-amber-900/30 text-amber-200 hover:bg-amber-800/40 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {bulkBusy ? '...' : `Suspender 24h (${selecionados.length})`}
          </button>
          <button
            type="button"
            onClick={() => aplicar('ban_perm')}
            disabled={selecionados.length === 0 || bulkBusy}
            className="px-4 py-2 text-sm rounded border border-red-700 bg-red-900/40 text-red-200 hover:bg-red-800/40 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {bulkBusy ? '...' : `Banir permanente (${selecionados.length})`}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : error ? (
        // Nada de estado-vazio junto do erro. "ninguém para banir" e "a
        // consulta falhou" levam a ações opostas; imprimir os dois deixa o
        // operador escolher a leitura errada — e a errada é a confortável.
        null
      ) : candidatos.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Nenhuma conta com evidência {minTier === 'confirmed' ? 'confirmada' : 'forte ou acima'}{' '}
          {rotuloDaJanela(period)}.
        </p>
      ) : (
        <div className="space-y-2">
          {candidatos.map((c) => (
            <CandidateCard
              key={chave(c)}
              c={c}
              selecionavel
              selected={selected.has(chave(c))}
              onToggle={() => toggle(c)}
            />
          ))}
        </div>
      )}

      {/* ============================================================
          EVIDENCIA SEM DONO VERIFICADO - investigacao, nunca ban
          ============================================================
          🔴 POR QUE ISTO E UMA SECAO E NAO UMA LINHA CINZA NA FILA DE CIMA.
            Foi assim que o adversario incriminou um inocente: declarar a conta
            da vitima custa 2 requests HTTP, e a fila antiga a mostrava no topo,
            "sustenta expulsao", com prova ao lado e caixa de selecao marcavel.
            Enquanto as duas coisas dividirem a MESMA lista, a diferenca entre
            elas depende de o operador reparar num badge as 3 da manha. */}
      {!loading && !error && investigar.length > 0 && (
        <section className="mt-8">
          <h3 className="text-lg font-bold text-amber-300">
            Evidência sem dono verificado ({investigar.length})
          </h3>
          <p className="text-xs text-gray-500 mt-1 mb-3 max-w-3xl">
            A prova é real. <strong className="text-amber-300">Quem</strong> ela acusa,
            não: a conta veio de uma afirmação do <em>cliente</em>{' '}
            (<code>ORACULO_SetAccountId</code>), que é o adversário — qualquer
            processo na máquina pode carimbar a conta de outra pessoa. Não há
            ação de ban aqui, e isso não é cautela: banir por esta lista é banir
            quem o atacante escolher. Serve como fila de investigação — abra a
            sessão. Vira acionável quando o servidor do jogo passar a confirmar
            o login (bind autenticado).
          </p>
          <div className="space-y-2">
            {investigar.map((c) => (
              <CandidateCard
                key={`inv:${chave(c)}`}
                c={c}
                selecionavel={false}
                selected={false}
                onToggle={() => {}}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default BanQueue;
